import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, ShieldAlert } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogTitle,
    DialogDescription,
} from "./ui/dialog";
import { DEPOSIT } from "../lib/testIds";
import { withholdDeposit } from "../lib/api";

const MAX_REASON = 1000;

export const WithholdDepositModal = ({ rental, open, onOpenChange, onWithheld }) => {
    const [reason, setReason] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (open) {
            setReason("");
            setSubmitting(false);
        }
    }, [open]);

    if (!rental) return null;

    const depositLabel =
        typeof rental.deposit === "number"
            ? rental.deposit.toLocaleString("en-US", {
                  style: "currency",
                  currency: "USD",
                  maximumFractionDigits: 2,
              })
            : "the deposit";

    const handleSubmit = async () => {
        const trimmed = reason.trim();
        if (!trimmed) {
            toast.error("Please add a reason");
            return;
        }
        try {
            setSubmitting(true);
            const updated = await withholdDeposit(rental.id, trimmed);
            toast.success("Deposit withheld", {
                description: "The renter has been notified with your reason. The withheld amount stays with you — Stripe will not refund it. If they dispute, you'll see it in Recent returns.",
                duration: 8000,
            });
            onWithheld?.(updated);
            onOpenChange(false);
        } catch (err) {
            toast.error("Couldn't withhold the deposit", {
                description:
                    err?.response?.data?.detail || "Please try again in a moment.",
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid={DEPOSIT.withholdModal}
                className="max-w-md rounded-3xl border border-[#2e3220]/10 bg-[#F4F1EA] p-7 text-[#2e3220]"
            >
                <DialogTitle className="flex items-center gap-2 font-display text-2xl font-bold text-[#2e3220]">
                    <ShieldAlert className="h-6 w-6 text-[#B56C5A]" strokeWidth={2.5} />
                    Withhold deposit
                </DialogTitle>
                <DialogDescription className="text-sm text-[#5a6142]">
                    You&apos;re withholding {depositLabel} from{" "}
                    <span className="font-semibold text-[#2e3220]">
                        {rental.renter_name}
                    </span>{" "}
                    for{" "}
                    <span className="font-semibold text-[#2e3220]">
                        {rental.item_name}
                    </span>
                    . They&apos;ll see your reason — be clear and fair.
                </DialogDescription>

                <div className="mt-5">
                    <label
                        htmlFor="withhold-reason"
                        className="text-[10px] font-bold uppercase tracking-widest text-[#737a3d]"
                    >
                        Reason (required)
                    </label>
                    <textarea
                        id="withhold-reason"
                        data-testid={DEPOSIT.withholdReason}
                        value={reason}
                        onChange={(e) =>
                            setReason(e.target.value.slice(0, MAX_REASON))
                        }
                        rows={5}
                        placeholder="e.g. Lens has a new crack on return (see photo 2). Asked renter on text, no response yet."
                        className="mt-2 w-full resize-none rounded-2xl border border-[#2e3220]/15 bg-white/70 px-4 py-3 text-sm text-[#2e3220] placeholder:text-[#5a6142]/60 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#B56C5A]"
                    />
                    <p className="mt-1 text-right text-[10px] text-[#5a6142]/70">
                        {reason.length}/{MAX_REASON}
                    </p>
                </div>

                <div className="mt-4 flex items-center justify-end gap-3">
                    <button
                        type="button"
                        data-testid={DEPOSIT.withholdCancel}
                        onClick={() => onOpenChange(false)}
                        disabled={submitting}
                        className="rounded-full border border-[#2e3220]/15 bg-transparent px-5 py-2 text-sm font-semibold text-[#2e3220] hover:bg-[#2e3220]/5 disabled:opacity-60"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        data-testid={DEPOSIT.withholdSubmit}
                        onClick={handleSubmit}
                        disabled={submitting || !reason.trim()}
                        className="inline-flex items-center gap-2 rounded-full bg-[#B56C5A] px-5 py-2 text-sm font-bold uppercase tracking-widest text-[#F4F1EA] hover:bg-[#9d5b4c] disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                        Withhold
                    </button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default WithholdDepositModal;
